import React from 'react';
import PropTypes from 'prop-types';
import { compose, withStateHandlers, mapProps, branch, withProps } from 'recompose';
import { List, OrderedMap } from 'immutable';
import moment from 'moment';

import WorkTypeName from '../../../../app/components/WorkTypeName';
import CustomerName from '../../../../app/components/CustomerName/CustomerName';
import Button from '../../../../app/components/Button';
import * as styles from '../styles.css';
import BackToTopButton from './BackToTopButton';
import withAbsentReasons from '../../../../app/containers/withAbsentReasons';
import requireProps from '../../../../app/lib/requireProps';
import { useTranslation } from 'react-i18next';

function Details({ groupedTimeplanUnits, displayBackButton, toggle, toggledOn, absentReasons }) {
  if (!groupedTimeplanUnits || groupedTimeplanUnits.size === 0) {
    return null;
  }

  const { t } = useTranslation();

  if (!toggledOn && toggle) {
    return (
      <Button
        icon="search-plus"
        onClick={toggle}
        variant="primary"
        stretch
      >
        {t("Visa detaljer")}
      </Button>
    );
  }

  return (
    <div>
      {toggle && (
        <Button
          icon="search-plus"
          onClick={toggle}
          variant="primary"
          stretch
        >
          {t("Dölj detaljer")}
        </Button>
      )}
      {groupedTimeplanUnits.entrySeq().map(([date, customers]) => (
        <div key={date}>
          <b>V.{moment(date).format('WW')} {moment(date).format('ddd DD/MM -YY')}</b>
          {customers.entrySeq().map(([customerId, timeplanUnits]) => (
            <div key={customerId} className={styles.timeplanDetails}>
              <b><CustomerName customerId={customerId} noLink /></b>
              <table className="table table-condensed">
                <tbody>
                  {timeplanUnits.map(tpu => (
                    <tr key={tpu.id}>
                      <td>
                        <WorkTypeName workTypeId={tpu.workType} />
                        {tpu.absentReason && (
                          <span>,
                            &nbsp;{absentReasons.find(({ id }) => id === tpu.absentReason).name}
                          </span>
                        )}
                      </td>
                      <td>{tpu.from} - {tpu.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ))}
      {displayBackButton && (
        <BackToTopButton />
      )}
    </div>
  );
}

Details.defaultProps = {
  groupedTimeplanUnits: new OrderedMap(),
  toggledOn: true,
  toggle: undefined,
};

Details.propTypes = {
  groupedTimeplanUnits: PropTypes.instanceOf(OrderedMap),
  displayBackButton: PropTypes.bool.isRequired,
  toggledOn: PropTypes.bool,
  toggle: PropTypes.func,
  absentReasons: PropTypes.instanceOf(List).isRequired,
};

// Group per day
// Group per customer
// Sort times

export default compose(
  withProps(({ timeplanUnits }) => ({
    public: true,
    timeplanUnits: timeplanUnits.sort((a, b) => {
      const first = moment(`${a.get('date')} ${a.get('to')}`);
      const second = moment(`${b.get('date')} ${b.get('to')}`);
      return first.diff(second);
    }),
    displayBackButton: List.isList(timeplanUnits) ?
      timeplanUnits.size > 10 : timeplanUnits.length > 10,
  })),
  withAbsentReasons,
  requireProps(['absentReasons']),
  mapProps(({ timeplanUnits, ...props }) => ({
    ...props,
    groupedTimeplanUnits: timeplanUnits.reduce((acc, tpu) => {
      const date = tpu.get('date');
      const customer = tpu.get('customer');
      if (acc.hasIn([date, customer])) {
        return acc.updateIn([date, customer], list => list.push(tpu));
      }
      return acc.setIn([date, customer], new List([tpu]));
    }, new OrderedMap()),
  })),
  branch(({ noToggle }) => !noToggle,
    withStateHandlers({ toggledOn: false }, {
      toggle: ({ toggledOn }) => () => ({ toggledOn: !toggledOn }),
    }),
  ),
)(Details);
